import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { LabelsDataService } from '@services/labels-data.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AbstractFormControlComponent } from '../abstract-form-control.component';
import { NameAutocompleteFormControl } from './name-autocomplete-form-control';

@Component({
  standalone: false,
  selector: 'app-name-autocomplete-form-control',
  templateUrl: './name-autocomplete-form-control.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NameAutocompleteFormControlComponent),
      multi: true
    }
  ]
})
export class NameAutocompleteFormControlComponent
  extends AbstractFormControlComponent<NameAutocompleteFormControl>
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() override parentForm: FormGroup = new FormGroup({});
  @Input() override formControlName: string = undefined;
  @Input() override title: string = undefined;
  @Input() autocompleteOptions: any[] = [];

  onChange: any = (value: string) => {};
  onTouched: any = () => {};
  requiredText: string = '';
  faCheck: any = faCheck;

  filteredOptions: Observable<any[]>;
  prevValue: string;

  constructor(public labelsDataService: LabelsDataService) {
    super();
  }

  ngOnInit(): void {
    this.requiredText = this.labelsDataService.getData(
      'COMMON.PleaseFillInThisFieldText'
    );
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public writeValue(value: any): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.autocompleteOptions &&
      changes?.autocompleteOptions.currentValue !== undefined
    ) {
      this.filteredOptions = this.parentForm
        .get(this.formControlName)
        .valueChanges.pipe(
          startWith(''),
          map(state =>
            state
              ? this._filterOptions(state)
              : this.autocompleteOptions?.slice()
          )
        );

      if (changes?.autocompleteOptions.currentValue.length > 0) {
        const value = this.parentForm.get(this.formControlName).value;

        this.filteredOptions = this.filteredOptions.pipe(
          map(item =>
            value
              ? this._filterOptions(value)
              : this.autocompleteOptions?.slice()
          )
        );
      }
    }
  }

  onInputValueChange(event: any): void {
    this.prevValue = this.parentForm.get(this.formControlName).value;

    this.filteredOptions = this.parentForm
      .get(this.formControlName)
      .valueChanges.pipe(
        startWith(''),
        map(state =>
          state ? this._filterOptions(state) : this.autocompleteOptions?.slice()
        )
      );
  }

  itemSelected(event: any): void {
    let selectedValue = event.option.value.allToUpperCase();
    let value = '';

    if (this.prevValue && this.prevValue.indexOf(' ') !== -1) {
      let splittedValue = this.prevValue.split(' ');
      let firstPart = splittedValue[0];
      if (firstPart.isNumeric()) {
        value = `${firstPart} `;
      }
    } else {
      if (this.prevValue?.isNumeric()) {
        value = `${this.prevValue} `.replace('  ', ' ');
      }
    }
    value += selectedValue;

    this.parentForm.get(this.formControlName).patchValue(value);
  }

  public get maxLength(): number {
    return this.control.maxLength;
  }

  public get readOnlyCss(): boolean {
    return this.control.readOnlyCss || false;
  }

  public get autocompleteLabelField(): string {
    return this.control.autocompleteLabelField;
  }

  public get escapeFirstDigits(): boolean {
    return this.control.escapeFirstDigits;
  }

  public get hideSuccessMark(): boolean {
    return this.control.hideSuccessMark || false;
  }

  private _filterOptions(value: string): any[] {
    let filterValue = value.toLowerCase();

    if (
      filterValue &&
      this.escapeFirstDigits &&
      filterValue.indexOf(' ') !== -1
    ) {
      let splittedValue = filterValue.split(' ');
      let firstPart = splittedValue[0];
      if (firstPart.isNumeric()) {
        filterValue = filterValue.replace(`${firstPart} `, '');
      }
    } else if (
      filterValue &&
      this.escapeFirstDigits &&
      filterValue.indexOf(' ') === -1
    ) {
      if (filterValue.isNumeric()) {
        filterValue = '';
      }
    }

    return this.autocompleteOptions?.filter(item =>
      item[this.autocompleteLabelField]?.toLowerCase().includes(filterValue)
    );
  }
}
